import React from 'react';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import { StyleSheet, css } from 'aphrodite';
import { Doughnut } from 'react-chartjs-2';

import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { ApiHelper } from '../../common/helpers/ApiHelper';
import { Helper } from '../../common/helpers/Helper';
import { GraphHelper } from '../../common/components/charts/GraphHelper';
import { TableComponent } from '../../common/components/TableComponent';
import { UiHelper } from '../../common/helpers/UiHelper';
import { Stats } from '../../components/Stats';
import { AppHelper } from '../../helpers/AppHelper';
import { AppStyles } from '../../styles/AppTheme';

const NO_VALUE_KEY = '_NO_VALUE';
const OTHERS_KEY = '_OTHERS';

const ISSUE_CONFIGS = {
  poorNetworkDevices: {
    title: 'Poor Network (<=2Bars or <20Mbps)',
    image: require('../../assets/icons/venueStatus/orientation.svg')
  },
  lowStorageDevices: {
    title: 'Low Storage Devices (20% or Less)',
    image: require('../../assets/icons/venueStatus/free-storage.svg')
  },
  lowRamDevices: {
    title: 'Low RAM Devices (40% or Less)',
    image: require('../../assets/icons/venueStatus/os-version.svg')
  }
};

const HEALTH_DONUT_PALETTE = [
  '#0057FF',
  '#FF9F1C',
  '#7B2CFF',
  '#00B3FF',
  '#FF4D8D',
  '#312E81',
  '#FFC300',
  '#8B5CF6',
  '#0891B2',
  '#C026D3',
  '#D97706',
  '#475569',
  '#9333EA',
  '#FBBF24',
  '#38BDF8',
  '#B45309',
  '#6B21A8',
  '#64748B'
];

export class VenueHealthDashboard extends BaseEntityPage {
  pageKey = 'VenueHealthDashboard';
  pageTitle = 'Venue Health';
  breadCrumbs = [
    { title: Helper.getString('home'), to: this.baseRoute() },
    { title: 'Venue Health' }
  ];
  noAdd = true;

  constructor(props, options) {
    super(props, options);
    this.embedded = Boolean(props.embedded);
    this.state = {
      ...this.state,
      dashboardData: {},
      showIssueDevicesDialog: false,
      issueDevicesFetchState: null,
      issueDevicesErrMsg: '',
      selectedIssueKey: '',
      selectedIssueTitle: '',
      selectedIssueDevices: []
    };
    this.chartOptions = GraphHelper.getdoughnutChartOptions();
    this.toFetch = this.makeFetches();
  }

  renderCustomReady() {
    if (!this.embedded) return null;
    return this.renderAboveTable();
  }

  makeFetches = () => {
    return [{
      key: 'venueHealth',
      method: 'GET',
      paths: ['miscs', 'venue-health']
    }];
  }

  onFetchSuccess(results) {
    this.setState({
      items: [],
      dashboardData: results[0] || {}
    });
  }

  openIssueDevicesDialog = (issueKey) => {
    const issueConfig = ISSUE_CONFIGS[issueKey];
    if (!issueConfig) return;

    this.setState({
      showIssueDevicesDialog: true,
      issueDevicesFetchState: ApiHelper.State.LOADING,
      issueDevicesErrMsg: '',
      selectedIssueKey: issueKey,
      selectedIssueTitle: issueConfig.title,
      selectedIssueDevices: []
    }, () => {
      ApiHelper.call({
        method: 'GET',
        paths: ['miscs', 'venue-health-issues'],
        queryParams: { issueType: issueKey }
      }, (err, result) => {
        if (err) {
          this.setState({
            issueDevicesFetchState: ApiHelper.State.ERROR,
            issueDevicesErrMsg: Helper.getErrorMsg(err),
            selectedIssueDevices: []
          });
          return;
        }

        this.setState({
          issueDevicesFetchState: ApiHelper.State.READY,
          issueDevicesErrMsg: '',
          selectedIssueDevices: result && result.items ? result.items : []
        });
      });
    });
  }

  closeIssueDevicesDialog = () => {
    this.setState({
      showIssueDevicesDialog: false,
      issueDevicesFetchState: null,
      issueDevicesErrMsg: '',
      selectedIssueKey: '',
      selectedIssueTitle: '',
      selectedIssueDevices: []
    });
  }

  buildStatsItems = () => {
    const data = this.state.dashboardData || {};
    const overview = data.overview || {};
    const registration = data.registration || {};
    const network = data.network || {};
    const storage = data.storage || {};
    const memory = data.memory || {};

    return [
      { title: 'Screens', value: overview.totalVenues || 0, image: require('../../assets/icons/venueStatus/app-version.svg') },
      // { title: 'Online Devices', value: overview.onlineDevices || 0 },
      // { title: 'Standby Devices', value: overview.standbyDevices || 0 },
      // { title: 'Offline Devices', value: overview.offlineDevices || 0 },
      {
        title: ISSUE_CONFIGS.poorNetworkDevices.title,
        value: network.poorNetworkDevices || 0,
        image: ISSUE_CONFIGS.poorNetworkDevices.image,
        showDetails: {
          onClick: () => this.openIssueDevicesDialog('poorNetworkDevices')
        }
      },
      {
        title: ISSUE_CONFIGS.lowStorageDevices.title,
        value: storage.lowStorageDevices || 0,
        image: ISSUE_CONFIGS.lowStorageDevices.image,
        showDetails: {
          onClick: () => this.openIssueDevicesDialog('lowStorageDevices')
        }
      },
      {
        title: ISSUE_CONFIGS.lowRamDevices.title,
        value: memory.lowRamDevices || 0,
        image: ISSUE_CONFIGS.lowRamDevices.image,
        showDetails: {
          onClick: () => this.openIssueDevicesDialog('lowRamDevices')
        }
      },
      // { title: 'Registered / Unregistered', value: `${registration.registeredDevices || 0} / ${registration.unregisteredDevices || 0}` }
    ];
  }

  buildDoughnutData = (chartTitle, countsByType, noValueLabel, maxSegments) => {
    const normalizedCounts = {
      ...(countsByType || {})
    };
    const entries = Object.keys(normalizedCounts)
      .filter((key) => key !== NO_VALUE_KEY)
      .map((key) => ({
        key,
        count: normalizedCounts[key] || 0
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);

    let chartEntries = entries;
    if (maxSegments && entries.length > maxSegments) {
      const topEntries = entries.slice(0, maxSegments);
      const othersCount = entries.slice(maxSegments).reduce((sum, item) => sum + item.count, 0);
      chartEntries = othersCount > 0
        ? topEntries.concat([{ key: OTHERS_KEY, count: othersCount }])
        : topEntries;
    }

    const labels = [];
    const values = [];

    chartEntries.forEach((item) => {
      const label = item.key === OTHERS_KEY ? 'Others' : item.key;
      labels.push(`${label} (${AppHelper.formatNumberStats(item.count)})`);
      values.push(item.count);
    });

    if (noValueLabel) {
      const noValueCount = normalizedCounts[NO_VALUE_KEY] || 0;
      if (noValueCount > 0) {
        labels.push(`${noValueLabel} (${AppHelper.formatNumberStats(noValueCount)})`);
        values.push(noValueCount);
      }
    }

    if (!values.length) return null;

    return GraphHelper.generateDoughNutGraphData({
      chartTitle,
      labels,
      data: values,
      palette: HEALTH_DONUT_PALETTE
    });
  }

  buildChartsData = () => {
    const data = this.state.dashboardData || {};
    const overview = data.overview || {};
    const registration = data.registration || {};
    const network = data.network || {};
    const deviceType = data.deviceType || {};
    const mdmType = data.mdmType || {};
    const riskLevel = data.riskLevel || {};
    const hwModel = data.hwModel || {};
    const osVersion = data.osVersion || {};
    const timezone = data.timezone || {};
    const countryCode = data.countryCode || {};
    const openOohCategory = data.openOohCategory || {};
    const orientation = data.orientation || {};
    const screenDimension = data.screenDimension || {};
    const hdmiState = data.hdmiState || {};
    const restrictionTags = data.restrictionTags || {};
    const pricingTier = data.pricingTier || {};
    const screenModel = data.screenModel || {};
    const screenBrand = data.screenBrand || {};
    const dpMediaOrientation = data.dpMediaOrientation || {};

    const statusCounts = {
      Online: overview.onlineDevices || 0,
      Standby: overview.standbyDevices || 0,
      Offline: overview.offlineDevices || 0
    };
    const registrationCounts = {
      Registered: registration.registeredDevices || 0,
      Unregistered: registration.unregisteredDevices || 0
    };
    const networkCounts = {
      ...(network.countsByType || {}),
      [NO_VALUE_KEY]: network.noNetworkTypeDevices || 0
    };
    const deviceTypeCounts = {
      ...(deviceType.countsByType || {}),
      [NO_VALUE_KEY]: deviceType.noDeviceTypeDevices || 0
    };
    const mdmTypeCounts = {
      ...(mdmType.countsByType || {}),
      [NO_VALUE_KEY]: mdmType.noMdmTypeDevices || 0
    };
    const riskLevelCounts = {
      ...(riskLevel.countsByType || {}),
      [NO_VALUE_KEY]: riskLevel.noRiskLevelDevices || 0
    };
    const hwModelCounts = {
      ...(hwModel.countsByType || {}),
      [NO_VALUE_KEY]: hwModel.noHwModelDevices || 0
    };
    const osVersionCounts = {
      ...(osVersion.countsByType || {}),
      [NO_VALUE_KEY]: osVersion.noOsVersionDevices || 0
    };
    const timezoneCounts = {
      ...(timezone.countsByType || {}),
      [NO_VALUE_KEY]: timezone.noTimezoneDevices || 0
    };
    const countryCodeCounts = {
      ...(countryCode.countsByType || {}),
      [NO_VALUE_KEY]: countryCode.noCountryCodeDevices || 0
    };
    const openOohCategoryCounts = {
      ...(openOohCategory.countsByType || {}),
      [NO_VALUE_KEY]: openOohCategory.noOpenOohCategoryDevices || 0
    };
    const orientationCounts = {
      ...(orientation.countsByType || {}),
      [NO_VALUE_KEY]: orientation.noOrientationDevices || 0
    };
    const screenDimensionCounts = {
      ...(screenDimension.countsByType || {}),
      [NO_VALUE_KEY]: screenDimension.noScreenDimensionDevices || 0
    };
    const hdmiStateCounts = {
      ...(hdmiState.countsByType || {}),
      [NO_VALUE_KEY]: hdmiState.noHdmiStateDevices || 0
    };
    const restrictionTagsCounts = {
      ...(restrictionTags.countsByType || {})
    };
    const pricingTierCounts = {
      ...(pricingTier.countsByType || {}),
      [NO_VALUE_KEY]: pricingTier.noPricingTierDevices || 0
    };
    const screenModelCounts = {
      ...(screenModel.countsByType || {}),
      [NO_VALUE_KEY]: screenModel.noScreenModelDevices || 0
    };
    const screenBrandCounts = {
      ...(screenBrand.countsByType || {}),
      [NO_VALUE_KEY]: screenBrand.noScreenBrandDevices || 0
    };
    const dpMediaOrientationCounts = {
      ...(dpMediaOrientation.countsByType || {}),
      [NO_VALUE_KEY]: dpMediaOrientation.noDpMediaOrientationDevices || 0
    };

    return [
      this.buildDoughnutData('Online vs Standby vs Offline', statusCounts),
      this.buildDoughnutData('Device Type', deviceTypeCounts, 'APK'),
      this.buildDoughnutData('MDM Type', mdmTypeCounts, 'None'),
      this.buildDoughnutData('Registered vs Unregistered', registrationCounts),
      this.buildDoughnutData('Risk Level', riskLevelCounts, 'None'),
      this.buildDoughnutData('Network Type', networkCounts, 'None'),
      this.buildDoughnutData('OS Version', osVersionCounts, 'None'),
      this.buildDoughnutData('Country Code', countryCodeCounts, 'None'),
      this.buildDoughnutData('Orientation', orientationCounts, 'None'),
      this.buildDoughnutData('Screen Dimension', screenDimensionCounts, 'Others'),
      this.buildDoughnutData('HDMI State', hdmiStateCounts, 'None'),
      this.buildDoughnutData('Restriction Tags', restrictionTagsCounts),
      this.buildDoughnutData('Pricing Tier', pricingTierCounts, 'None'),
      this.buildDoughnutData('Screen Model', screenModelCounts, 'None'),
      this.buildDoughnutData('Screen Brand', screenBrandCounts, 'None'),
      this.buildDoughnutData('DP Media Orientation', dpMediaOrientationCounts, 'None'),
      this.buildDoughnutData('Model', hwModelCounts, 'None', 7),
      this.buildDoughnutData('Timezone', timezoneCounts, 'None', 7),
      this.buildDoughnutData('OpenOOH Category', openOohCategoryCounts, 'None', 7)
    ].filter(Boolean);
  }

  formatPercent = (value) => {
    const numberValue = Number(value);
    if (value === null || value === undefined || Number.isNaN(numberValue)) return '-';
    return `${Math.round(numberValue * 10) / 10}%`;
  }

  formatNetworkType = (value) => {
    const networkType = String(value || '').trim().toUpperCase();
    if (!networkType) return '-';
    if (networkType === 'MOBILE') return 'CELLULAR';
    return networkType;
  }

  formatNumberValue = (value, suffix = '') => {
    const numberValue = Number(value);
    if (value === null || value === undefined || Number.isNaN(numberValue)) return '-';
    return `${Math.round(numberValue * 10) / 10}${suffix}`;
  }

  getIssueTableHeadersMap = () => {
    const commonHeaders = {
      '#': (current) => current.__index + 1,
      'Screen Code': (current) => {
        return (
          <div>
            {current.deviceCode ? <div>{current.deviceCode}</div> : null}
            {current.siteTitle ? <div>{current.siteTitle}</div> : null}
            {current.siteAddress ? <div>{current.siteAddress}</div> : null}
          </div>
        )
      }
    };

    if (this.state.selectedIssueKey === 'poorNetworkDevices') {
      return {
        ...commonHeaders,
        'Network Type': (current) => this.formatNetworkType(current.networkType),
        'WiFi Signal': (current) => this.formatNumberValue(current.wifiSignalStrength),
        'WiFi Speed (Mbps)': (current) => this.formatNumberValue(current.wifiSpeed),
        'Cellular Signal': (current) => this.formatNumberValue(current.cellularSignalStrength)
      };
    }

    if (this.state.selectedIssueKey === 'lowStorageDevices') {
      return {
        ...commonHeaders,
        'Free Storage': (current) => this.formatPercent(current.freePercent)
      };
    }

    return {
      ...commonHeaders,
      'Available RAM': (current) => this.formatPercent(current.ramFreePercent)
    };
  }

  renderIssueDevicesDialog = () => {
    const { showIssueDevicesDialog, issueDevicesFetchState, selectedIssueTitle, selectedIssueDevices } = this.state;
    if (!showIssueDevicesDialog) return null;

    return (
      <Dialog
        open={showIssueDevicesDialog}
        aria-labelledby={selectedIssueTitle}
        fullWidth={true}
        fullScreen={AppHelper.fullWidthCondition()}
        maxWidth='md'
        classes={{
          root: css(Styles.dialogRoot),
          paper: css(Styles.dialogPaper),
        }}
      >
        <div className={css([Styles.modalHeader, AppStyles.modalHeader])}>
          <div className={css(Styles.modalTitle)}>{selectedIssueTitle}</div>
          <button className={css(Styles.modalCloseButton)} onClick={this.closeIssueDevicesDialog}>
            {UiHelper.renderImg({ key: 'dialogCloseIcon' })}
          </button>
        </div>
        <div className={css(Styles.modalContent)}>
          {issueDevicesFetchState === ApiHelper.State.LOADING
            ? <div className={css(Styles.stateContainer)}>{UiHelper.componentLoadingView()}</div>
            : issueDevicesFetchState === ApiHelper.State.ERROR
              ? <div className={css(Styles.stateContainer, Styles.errorText)}>{this.state.issueDevicesErrMsg || Helper.getString('defaultApiErrMsg')}</div>
              : !selectedIssueDevices.length
                ? UiHelper.emptyView()
                : (
                  <div className={css(Styles.tableContainer)}>
                    <TableComponent
                      compact
                      tableHeadersMap={this.getIssueTableHeadersMap()}
                      items={selectedIssueDevices}
                      noTableHead={false}
                      onActions={() => []}
                      bodyCellStyle={Styles.issueBodyCell}
                    />
                  </div>
                )}
        </div>
      </Dialog>
    );
  }

  renderAboveTable() {
    const chartsData = this.buildChartsData();

    return (
      <div className={css(Styles.mainContainer)}>
        <div className={css(Styles.sectionBlock)}>
          <div className={css(Styles.statsWrap)}>
            <Stats items={this.buildStatsItems()} />
          </div>
        </div>

        <div className={css(Styles.latencyNoteWrap)}>
          <div className={css(Styles.latencyNote)}>
            {Helper.getString('screenHealthReportLatencyNote')}
          </div>
        </div>

        <div className={css(Styles.sectionBlock)}>
          {chartsData.length > 0
            ? (
              <Grid className={css(Styles.chartsContainer)} container spacing={2}>
                {chartsData.map((chart, index) => (
                  <Grid item key={`chart_${index}`} className={css(Styles.eachChartHolder)} xs={12} md={4}>
                    <div className={css(Styles.chartOuterWrap)}>
                      <div className={css(Styles.chartBlock)}>
                        {chart.chartTitle &&
                          <div className={css(Styles.chartTitle)}>{chart.chartTitle}</div>
                        }
                        <Doughnut
                          className={css(Styles.chartCanvas)}
                          data={chart.data}
                          options={this.chartOptions}
                          width={100}
                          height={100}
                        />
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            )
            : (
              <div className={css(Styles.noChartMessage)}>
                {Helper.getString('noChartDataMessage')}
              </div>
            )}
        </div>
        {this.renderIssueDevicesDialog()}
      </div>
    );
  }
}

const Styles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    marginTop: 20
  },
  sectionBlock: {
    width: '100%',
    marginBottom: 28
  },
  statsWrap: {
    width: '100%'
  },
  latencyNoteWrap: {
    margin: '4px 0 14px'
  },
  latencyNote: {
    fontSize: 14,
    color: '#040BC5'
  },
  chartsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    height: 'auto',
    width: '100%'
  },
  eachChartHolder: {},
  chartOuterWrap: {
    background: '#fff',
    borderRadius: 16,
    minHeight: 260,
    boxShadow: '0 8px 24px rgba(14, 30, 37, 0.06)'
  },
  chartBlock: {
    height: 220,
    padding: '14px',
    boxSizing: 'border-box'
  },
  chartCanvas: {
    width: '100% !important',
    height: '100% !important'
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 10,
    color: '#121212'
  },
  noChartMessage: {
    width: '100%',
    minHeight: 180,
    background: '#fff',
    borderRadius: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#6b7280'
  },
  dialogRoot: {
    zIndex: 13000,
  },
  dialogPaper: {
    width: '100%',
    minWidth: 900,
    borderRadius: 40,
    '@media (max-width: 1024px)': {
      minWidth: 'unset',
      maxHeight: 'calc(100% - 140px) !important',
      borderRadius: '20px 20px 0 0',
      bottom: '0 !important',
      position: 'fixed',
      height: 'auto !important'
    }
  },
  modalHeader: {
    padding: 20,
    paddingLeft: 30,
    paddingRight: 30,
    paddingBottom: 14
  },
  modalTitle: {
    color: 'black',
    fontSize: 26,
    fontWeight: 600
  },
  modalCloseButton: {
    position: 'absolute',
    top: 23,
    right: 30,
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    outline: 'none',
    cursor: 'pointer',
  },
  modalContent: {
    paddingLeft: 30,
    paddingRight: 30,
    paddingBottom: 30,
    minHeight: 260,
  },
  tableContainer: {
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  issueBodyCell: {
    backgroundColor: '#F1F3F5',
    borderColor: '#F1F3F5',
  },
  stateContainer: {
    maxHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  errorText: {
    color: '#D14343',
    textAlign: 'center'
  }
});



// WEBPACK FOOTER //
// ./src/screens/admin/VenueHealthDashboard.js